/**
 * useStewardTurn — drives the workspace thread over the SSE turn endpoint.
 *
 * Owns the thread state so StewardWorkspace can stay presentational: it appends
 * the user's message, streams the assistant's prose into place, and attaches
 * cards/chips as their action frames arrive.
 *
 * NO AUTO-RETRY after a mid-stream drop. A turn that died halfway has already
 * been persisted server-side, so silently re-sending it would double the
 * traveler's question and could double-charge the model. The thread shows the
 * failure and the traveler decides.
 */
import { useCallback, useRef, useState } from "react";
import { stewardApi } from "@/lib/api";
import type {
  StewardCardData,
  StewardDayPlanData,
  StewardHistoryMessage,
  StewardMessage,
  StewardScope,
} from "@/lib/types";

/**
 * The ONLY thing a failed turn is allowed to say.
 *
 * Matches the server's own mid-stream error frame word for word, so a transport
 * failure and a model failure read as the same event to the traveler.
 */
export const TURN_ERROR_LINE = "I lost my train of thought there — ask me again?";

/**
 * What the thread shows when the stream throws — or null when there is nothing
 * to show because the traveler simply closed the sheet.
 *
 * ⚠️ IT NEVER RETURNS `err.message`, AND THAT IS THE WHOLE POINT. The previous
 * version did, and a traveler watched the literal words "network error" appear
 * in red inside their conversation with Steward. `stewardApi.turn` also throws
 * server-supplied text on a non-2xx, so `err.message` is an arbitrary transport
 * string from at least two directions. The real message goes to the console for
 * whoever is debugging; the traveler gets the persona line, always.
 */
export function turnErrorLine(err: unknown): string | null {
  // An aborted stream is the user closing the sheet, not a failure.
  if ((err as Error)?.name === "AbortError") return null;
  return TURN_ERROR_LINE;
}

export interface UseStewardTurn {
  messages: StewardMessage[];
  streaming: boolean;
  /** The live status line between turn phases ("Checking sources…"). */
  status: string | null;
  send: (message: string, scope?: StewardScope | null) => Promise<void>;
  /** Seed the thread with the cached opener (assistant turn, no round trip). */
  seedOpener: (text: string, chips: string[]) => void;
  /** Replace an empty thread with the persisted history. Returns what it did. */
  hydrate: (history: StewardHistoryMessage[]) => void;
  /** True once hydrate() has run — the opener must wait for it. */
  hydrated: boolean;
  reset: () => void;
}

export function useStewardTurn(
  tripId: string,
  initialConversationId?: string | null,
): UseStewardTurn {
  const [messages, setMessages] = useState<StewardMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const conversationId = useRef<string | null>(initialConversationId ?? null);
  const abort = useRef<AbortController | null>(null);

  /**
   * Load the persisted thread back into view.
   *
   * Guarded on emptiness because hydration and the first turn can race: the
   * traveler can tap a banner chip before the history request lands, and a
   * late history response must never overwrite a turn that is already
   * streaming. Only the chips on the LAST turn survive — older chips are
   * choices the traveler has already moved past.
   */
  const hydrate = useCallback((history: StewardHistoryMessage[]) => {
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return history.map((m, i) => ({
        role: m.role,
        content: m.content,
        cards: m.cards ?? [],
        // A day survives a reload the same way a card does. `?? []` matters:
        // a message written before @@DAY@@ existed has no `days` key at all.
        days: m.days ?? [],
        chips: i === history.length - 1 ? (m.chips ?? []) : [],
      }));
    });
    setHydrated(true);
  }, []);

  const seedOpener = useCallback((text: string, chips: string[]) => {
    setMessages((prev) =>
      prev.length > 0
        ? prev
        : [{ role: "assistant", content: text, cards: [], days: [], chips }],
    );
  }, []);

  const reset = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setMessages([]);
    setStreaming(false);
    setStatus(null);
    setHydrated(false);
  }, []);

  const send = useCallback(
    async (message: string, scope?: StewardScope | null) => {
      const text = message.trim();
      if (!text || streaming) return;

      abort.current = new AbortController();
      setStreaming(true);
      setStatus(null);

      // Optimistically show the traveler's message, and clear the chips on the
      // previous turn — they've made their choice, stale chips are clutter.
      setMessages((prev) => [
        ...prev.map((m) => ({ ...m, chips: [] })),
        { role: "user" as const, content: text, cards: [], days: [], chips: [] },
        {
          role: "assistant" as const,
          content: "",
          cards: [],
          days: [],
          chips: [],
          blocks: [],
          pending: true,
        },
      ]);

      /** Mutate the in-flight assistant turn (always the last message). */
      const patchLast = (fn: (m: StewardMessage) => StewardMessage) =>
        setMessages((prev) => {
          if (prev.length === 0) return prev;
          const next = prev.slice();
          next[next.length - 1] = fn(next[next.length - 1]);
          return next;
        });

      try {
        for await (const event of stewardApi.turn(
          tripId,
          { message: text, conversationId: conversationId.current, scope: scope ?? null },
          abort.current.signal,
        )) {
          if (event.type === "conversation") {
            conversationId.current = event.conversationId;
          } else if (event.type === "text") {
            patchLast((m) => ({ ...m, content: m.content + event.text, pending: false }));
            setStatus(null);
          } else if (event.type === "status") {
            setStatus(event.text);
          } else if (event.type === "action") {
            const action = event.action;
            if (action.kind === "card") {
              const card: StewardCardData = action.card;
              patchLast((m) =>
                m.cards.some((c) => c.card_key === card.card_key)
                  ? m
                  : {
                      ...m,
                      cards: [...m.cards, card],
                      blocks: [...(m.blocks ?? []), { kind: "card", key: card.card_key }],
                      pending: false,
                    },
              );
            } else if (action.kind === "dayplan") {
              const day: StewardDayPlanData = action.day;
              patchLast((m) => {
                const at = m.days.findIndex((d) => d.day_date === day.day_date);
                // A re-emitted date is a REDRAW, not a second day — the server
                // stripper takes the latest for the same reason, because a model
                // correcting itself mid-reply means the correction. Keeping both
                // would offer the traveler the same day twice.
                if (at >= 0) {
                  const days = m.days.slice();
                  days[at] = day;
                  return { ...m, days, pending: false };
                }
                return {
                  ...m,
                  days: [...m.days, day],
                  blocks: [...(m.blocks ?? []), { kind: "day", key: day.day_date }],
                  pending: false,
                };
              });
            } else if (action.kind === "chips") {
              patchLast((m) => ({ ...m, chips: action.chips }));
            } else if (action.kind === "status") {
              setStatus(action.text);
            }
          } else if (event.type === "changes") {
            // Steward noticed the trip moved under the conversation. It belongs
            // ON the turn, above her answer, so the correction and the reason
            // for it arrive together instead of as an unexplained change of mind.
            patchLast((m) => ({ ...m, changes: event.lines }));
          } else if (event.type === "error") {
            patchLast((m) => ({ ...m, error: event.text, pending: false }));
          }
        }
      } catch (err) {
        const line = turnErrorLine(err);
        if (line) {
          // The real reason goes HERE, where an engineer can find it — never
          // into the thread, where it reads as Steward saying "network error".
          console.error("[steward] turn failed:", (err as Error)?.message ?? err);
          patchLast((m) => ({ ...m, pending: false, error: line }));
        }
      } finally {
        setStreaming(false);
        setStatus(null);
        abort.current = null;
      }
    },
    [tripId, streaming],
  );

  return { messages, streaming, status, send, seedOpener, hydrate, hydrated, reset };
}
